import { useCallback, useEffect, useState } from "react";
import PageContainer from "@components/layout/PageContainer";
import { Button, OffcanvasBody, OffcanvasHeader } from "reactstrap";
import DefaultOffCanvas from "@components/layout/Offcanvas/DefaultOffCanvas";
import LinkCategoryForm from "@components/forms/LinkCategoryForm";
import LinkItemForm from "@components/forms/LinkItemForm";
import { LinkCategoryApi, LinkCategoryDataType } from "@/api/LinkCategoryApi";
import { LinkItemApi, LinkItemDataType } from "@/api/LinkItemApi";
import { showAlert } from "@utils/Alert";
import LoadingScreen from "@components/content/LoadingScreen";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrash, faPlus, faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";
import ConfirmableButton from "@components/content/ConfirmableButton";
import Masonry from "react-masonry-css";
import styles from "./style.module.scss";

/** 3 columnas en escritorio; reparto tipo mosaico (react-masonry-css). */
const LINKS_MASONRY_BREAKPOINTS = {
    default: 3,
    992: 2,
    576: 1,
};

type CategoryOffcanvasState = { open: boolean; id?: string };
type LinkOffcanvasState = { open: boolean; id?: string; linkCategoryId: string };

const CATEGORY_FORM_ID = "linkCategoryOffcanvasForm";
const LINK_FORM_ID = "linkItemOffcanvasForm";

const LinksIndex = () => {
    const [loading, setLoading] = useState(true);
    const [categories, setCategories] = useState<LinkCategoryDataType[]>([]);
    const [itemsByCategory, setItemsByCategory] = useState<Record<string, LinkItemDataType[]>>({});

    const [isEditMode, setIsEditMode] = useState(false);

    const [categoryOffcanvas, setCategoryOffcanvas] = useState<CategoryOffcanvasState>({ open: false });
    const [linkOffcanvas, setLinkOffcanvas] = useState<LinkOffcanvasState>({
        open: false,
        linkCategoryId: "",
    });

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [catRes, itemRes] = await Promise.all([
                LinkCategoryApi.fetchAll({}, "", 5000),
                LinkItemApi.fetchAll({}, "", 5000),
            ]);
            const sortedCats = [...(catRes.items || [])].sort((a, b) =>
                (a.title || "").localeCompare(b.title || "", "es", { sensitivity: "base" }),
            );
            setCategories(sortedCats);
            const map: Record<string, LinkItemDataType[]> = {};
            for (const item of itemRes.items || []) {
                const cid = item.linkCategoryId || "";
                if (!map[cid]) map[cid] = [];
                map[cid].push(item);
            }
            for (const k of Object.keys(map)) {
                map[k].sort((a, b) =>
                    (a.title || "").localeCompare(b.title || "", "es", { sensitivity: "base" }),
                );
            }
            setItemsByCategory(map);
        } catch (e) {
            showAlert("danger", "No fue posible cargar los enlaces");
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void loadData();
    }, [loadData]);

    useEffect(() => {
        if (!isEditMode) {
            setCategoryOffcanvas({ open: false });
            setLinkOffcanvas({ open: false, id: undefined, linkCategoryId: "" });
        }
    }, [isEditMode]);

    const closeCategoryOffcanvas = () => setCategoryOffcanvas({ open: false });

    const closeLinkOffcanvas = () =>
        setLinkOffcanvas({ open: false, id: undefined, linkCategoryId: "" });

    const onCategoryStored = () => {
        closeCategoryOffcanvas();
        void loadData();
    };

    const onLinkStored = () => {
        closeLinkOffcanvas();
        void loadData();
    };

    const deleteCategory = async (categoryId: string) => {
        try {
            await LinkCategoryApi.deleteCategoryWithItems(categoryId);
            showAlert("success", "La categoría y sus enlaces fueron eliminados");
            await loadData();
        } catch {
            showAlert("danger", "No fue posible eliminar la categoría");
        }
    };

    const deleteLink = async (linkId: string) => {
        try {
            await LinkItemApi.delete(linkId);
            showAlert("success", "El enlace fue eliminado");
            await loadData();
        } catch {
            showAlert("danger", "No fue posible eliminar el enlace");
        }
    };

    const openNewCategory = () => {
        if (!isEditMode) return;
        setCategoryOffcanvas({ open: true });
    };

    const toggleEditMode = () => setIsEditMode((v) => !v);

    const openEditCategory = (id: string) => {
        if (!isEditMode) return;
        setCategoryOffcanvas({ open: true, id });
    };

    const openNewLink = (linkCategoryId: string) => {
        if (!isEditMode) return;
        setLinkOffcanvas({ open: true, linkCategoryId, id: undefined });
    };

    const openEditLink = (id: string, linkCategoryId: string) => {
        if (!isEditMode) return;
        setLinkOffcanvas({ open: true, id, linkCategoryId });
    };

    return (
        <>
            <PageContainer
                title="Enlaces"
                backUrl="/user/dashboard"
                primaryActions={[
                    {
                        actionTitle: isEditMode ? "Listo" : "Editar",
                        color: isEditMode ? "secondary" : "primary",
                        onClick: toggleEditMode,
                    },
                    {
                        actionTitle: "Nueva Categoría",
                        color: "success",
                        onClick: openNewCategory,
                        disabled: !isEditMode,
                    },
                ]}
            >
                {categories.length === 0 && !loading ? (
                    <p className={styles.emptyState}>
                        {isEditMode
                            ? "No hay categorías todavía. Use «Nueva Categoría» para comenzar."
                            : "No hay categorías todavía. Pulse «Editar» para agregar o modificar categorías y enlaces."}
                    </p>
                ) : (
                    <Masonry
                        breakpointCols={LINKS_MASONRY_BREAKPOINTS}
                        className={styles.masonryGrid}
                        columnClassName={styles.masonryColumn}
                    >
                        {categories.map((cat) => {
                            const cid = cat.id || "";
                            const links = itemsByCategory[cid] || [];
                            return (
                                <div key={cid || cat.title} className={styles.cardWrap}>
                                    <div className={styles.card}>
                                        <div className={styles.cardHeader}>
                                            <h3 className={styles.cardTitle}>{cat.title}</h3>
                                            <div className={styles.headerActions}>
                                                {isEditMode && cid ? (
                                                    <>
                                                        <Button
                                                            className={styles.iconBtn}
                                                            color="link"
                                                            size="sm"
                                                            title="Editar categoría"
                                                            onClick={() => openEditCategory(cid)}
                                                            type="button"
                                                        >
                                                            <FontAwesomeIcon icon={faEdit} />
                                                        </Button>
                                                        <ConfirmableButton
                                                            trigger={
                                                                <Button
                                                                    className={styles.iconBtn}
                                                                    color="link"
                                                                    size="sm"
                                                                    title="Eliminar categoría"
                                                                    type="button"
                                                                >
                                                                    <FontAwesomeIcon icon={faTrash} />
                                                                </Button>
                                                            }
                                                            title="Eliminar categoría"
                                                            confirmMessage="¿Seguro que desea eliminar esta categoría y todos sus enlaces?"
                                                            confirmBtnText="Eliminar"
                                                            primaryBtnColor="danger"
                                                            onConfirm={() => void deleteCategory(cid)}
                                                        />
                                                    </>
                                                ) : null}
                                            </div>
                                        </div>
                                        <ul className={styles.linkList}>
                                            {links.map((link) => {
                                                const href =
                                                    typeof link.url === "string" && link.url.trim()
                                                        ? link.url.includes("://")
                                                            ? link.url.trim()
                                                            : `https://${link.url.trim()}`
                                                        : "#";
                                                return (
                                                    <li key={link.id || link.title} className={styles.linkRow}>
                                                        <a
                                                            className={styles.linkAnchor}
                                                            href={href}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                        >
                                                            <span className={styles.linkTitleText}>{link.title}</span>
                                                            <FontAwesomeIcon
                                                                className={styles.externalIcon}
                                                                icon={faExternalLinkAlt}
                                                                size="sm"
                                                            />
                                                        </a>
                                                        {isEditMode ? (
                                                            <div className={styles.linkRowActions}>
                                                                {link.id ? (
                                                                    <>
                                                                        <Button
                                                                            color="link"
                                                                            size="sm"
                                                                            title="Editar enlace"
                                                                            type="button"
                                                                            onClick={() =>
                                                                                openEditLink(link.id!, cid)
                                                                            }
                                                                        >
                                                                            <FontAwesomeIcon icon={faEdit} />
                                                                        </Button>
                                                                        <ConfirmableButton
                                                                            trigger={
                                                                                <Button
                                                                                    color="link"
                                                                                    size="sm"
                                                                                    title="Eliminar enlace"
                                                                                    type="button"
                                                                                >
                                                                                    <FontAwesomeIcon icon={faTrash} />
                                                                                </Button>
                                                                            }
                                                                            title="Eliminar enlace"
                                                                            confirmMessage="¿Seguro que desea eliminar este enlace?"
                                                                            confirmBtnText="Eliminar"
                                                                            primaryBtnColor="danger"
                                                                            onConfirm={() =>
                                                                                link.id
                                                                                    ? void deleteLink(link.id)
                                                                                    : undefined
                                                                            }
                                                                        />
                                                                    </>
                                                                ) : null}
                                                            </div>
                                                        ) : null}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                        {isEditMode && cid ? (
                                            <div className={styles.addLinkRow}>
                                                <Button
                                                    color="success"
                                                    size="sm"
                                                    type="button"
                                                    onClick={() => openNewLink(cid)}
                                                >
                                                    <FontAwesomeIcon className="me-1" icon={faPlus} />
                                                    Nuevo enlace
                                                </Button>
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                            );
                        })}
                    </Masonry>
                )}
            </PageContainer>

            <DefaultOffCanvas
                isOpen={categoryOffcanvas.open}
                toggle={closeCategoryOffcanvas}
                direction="end"
            >
                <OffcanvasHeader className={styles.offCanvasHeader} toggle={closeCategoryOffcanvas}>
                    {categoryOffcanvas.id ? "Editar categoría" : "Nueva categoría"}
                </OffcanvasHeader>
                <OffcanvasBody>
                    {categoryOffcanvas.open ? (
                        <>
                            <LinkCategoryForm
                                key={categoryOffcanvas.id ?? "create"}
                                id={categoryOffcanvas.id}
                                formId={CATEGORY_FORM_ID}
                                onStored={onCategoryStored}
                            />
                            <Button
                                className="mt-3"
                                color="success"
                                type="submit"
                                form={CATEGORY_FORM_ID}
                            >
                                Guardar
                            </Button>
                        </>
                    ) : null}
                </OffcanvasBody>
            </DefaultOffCanvas>

            <DefaultOffCanvas isOpen={linkOffcanvas.open} toggle={closeLinkOffcanvas} direction="end">
                <OffcanvasHeader className={styles.offCanvasHeader} toggle={closeLinkOffcanvas}>
                    {linkOffcanvas.id ? "Editar enlace" : "Nuevo enlace"}
                </OffcanvasHeader>
                <OffcanvasBody>
                    {linkOffcanvas.open ? (
                        <>
                            <LinkItemForm
                                key={linkOffcanvas.id ?? `create-${linkOffcanvas.linkCategoryId}`}
                                id={linkOffcanvas.id}
                                formId={LINK_FORM_ID}
                                defaultData={
                                    !linkOffcanvas.id && linkOffcanvas.linkCategoryId
                                        ? { linkCategoryId: linkOffcanvas.linkCategoryId }
                                        : undefined
                                }
                                onStored={onLinkStored}
                            />
                            <Button className="mt-3" color="success" type="submit" form={LINK_FORM_ID}>
                                Guardar
                            </Button>
                        </>
                    ) : null}
                </OffcanvasBody>
            </DefaultOffCanvas>

            <LoadingScreen isLoading={loading} />
        </>
    );
};

export default LinksIndex;
